/**
 * @package
 * @author Artem Ilinykh devsinglesly@gmail.com
 * @class BuildingsModule
 */
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { Building } from '@/modules/backend/Entities/Building';
import { CreateBuildingData } from '@/modules/backend/Dto/CreateBuildingData';
import homeSmartApi from '@/plugins/HomeSmartApi';
import { QueryBuildingsData } from '@/modules/backend/Dto/QueryBuildingsData';

@Module({ store, namespaced: true, name: 'dashboard/buildings' })
export class BuildingsModule extends VuexModule {
  private loading = false;
  private items: Record<number, Building[]> = {};

  public get Buildings(): Building[] {
    return Object.values(this.items).flat();
  }

  public get BuildingsByCompanyId(): (companyId: number) => Building[] {
    return (companyId: number): Building[] => (this.items[companyId] ?? []);
  }

  public get BuildingById(): (buildingId: number) => Building | undefined {
    return (buildingId: number): Building | undefined => {
      return Object.entries(this.items).reduce((accum, [, items]) => {
        if (accum) {
          return accum;
        }

        return items.find((building) => building.getId() === buildingId);
      }, undefined as undefined | Building);
    };
  }

  @Action
  public async Create(payload: CreateBuildingData): Promise<Building> {
    const result = await homeSmartApi.Buildings.Create(payload);
    this.context.commit('add', result);
    return result;
  }

  @Action
  public async Fetch(query: number | QueryBuildingsData): Promise<Building[]> {
    if (typeof query === 'number' && Number.isInteger(query)) {
      query = new QueryBuildingsData().fromPlain({
        companies: [query as number]
      });
    }

    if (!query as unknown instanceof QueryBuildingsData) {
      throw new Error('Transform error');
    }

    const result = await homeSmartApi.Buildings.fetch(query as QueryBuildingsData);
    this.context.commit('set', result);
    return result;
  }

  @Action
  public async FetchSingle(buildingId: number): Promise<Building | undefined> {
    const response = await homeSmartApi.Buildings.GetOne(buildingId);
    this.context.commit('add', response);
    return response;
  }

  @Action
  public async Remove(buildingId: number): Promise<void> {
    await homeSmartApi.Buildings.Remove(buildingId);

    this.context.commit('RemoveById', buildingId);
  }

  @Mutation
  private changeLoading(loading: boolean): void {
    this.loading = loading;
  }

  @Mutation
  private set(payload: Building[]): void {
    const items = payload.reduce((accum, item) => {
      accum[item.getCompany().getId()] = [
        ...accum[item.getCompany().getId()] || [],
        item
      ];

      return accum;
    }, {} as {[id: number]: Building[]});

    this.items = { ...this.items, ...items };
  }

  @Mutation
  private add(building: Building): void {
    const companyId = building.getCompany().getId();

    if (!Object.prototype.hasOwnProperty.call(this.items, companyId)) {
      this.items = {
        ...this.items,
        [companyId]: [building]
      };
      return;
    }

    const existingIds = this.items[companyId].map(item => item.getId());
    if (existingIds.indexOf(building.getId()) === -1) {
      this.items = {
        ...this.items,
        [companyId]: [
          ...this.items[companyId],
          building
        ]
      };
    }
  }

  @Mutation
  private RemoveById(buildingId: number): void {
    this.items = Object.fromEntries(Object.entries(this.items).map((entries) => ([
      entries[0], entries[1].filter((building) => building.getId() !== buildingId)
    ])));
  }
}
